import axios from "axios";
import { PrimaryCta } from "components/Buttons";
import ErrorToast from "components/Customs/ErrorToast";
import { TextField } from "components/Fields";
import { SelectFieldNew } from "components/Fields/SelectField";
import { MONTHS_OPTIONS, UNDEFINED } from "constants/forms";
import { Formik } from "formik";
import { generateRandom6AlphaNumeric } from "helpers";
import useTracker from "hooks/useTracker";
import { MSMProfile, QuickWidgetResult } from "model/object/quick-widget-result";
import { estimateSchemaGrey } from "model/schema/estimate";
import { ChangeEvent, Dispatch, SetStateAction, useRef, useState } from "react";
import { Form } from "react-bootstrap";

const QWPartnerForm = ({
	reg,
	setStep,
	setResults,
	setProfile,
	setReg,
	variant = "partner",
}: {
	reg: string;
	setStep: Dispatch<SetStateAction<number>>;
	setResults: Dispatch<SetStateAction<QuickWidgetResult>>;
	setProfile: Dispatch<SetStateAction<MSMProfile>>;
	source?: string;
	setReg: Dispatch<SetStateAction<string>>;
	variant?: string;
}) => {
	const [errorSubmit, setErrorSubmit] = useState("");
	const [open, setOpen] = useState(false);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);
	const formikRef = useRef<any>(null);
	const track = useTracker();
	const numbers = generateRandom6AlphaNumeric();

	const errorKeys = (key: string) => {
		let temp = "";
		switch (key) {
			case "registration":
				temp = "Registration";
				break;
			case "noClaimsDuration":
				temp = "No claims bonus";
				break;
			case "driverAge":
				temp = "Age";
				break;
			case "postcode":
				temp = "Postcode";
				break;
			case "renewalMonth":
				temp = "Renewal Month";
				break;
			default:
				temp = "Postcode";
		}

		return temp;
	};

	const getFormikErrors = (errors: { [key: string]: any }): string[] => {
		console.log(errors);
		const errorMessages: string[] = [];

		const extractErrors = (errorObject: any, path: string[] = []) => {
			Object.keys(errorObject).forEach(key => {
				const value = errorObject[key];
				if (typeof value === "string") {
					errorMessages.push(`${path.concat(errorKeys(key)).join(" -> ")}: ${value}`);
				} else if (typeof value === "object" && value !== null) {
					extractErrors(value, path.concat(errorKeys(key)));
				}
			});
		};

		extractErrors(errors);
		return errorMessages;
	};

	const handleChangeUppercase = (e: ChangeEvent<any>, setFieldValue: (field: string, value: string) => void) => {
		const { value } = e.target;
		// Convert the input value to uppercase
		const uppercaseValue = value.toUpperCase();
		// Set the uppercase value using Formik's setFieldValue
		setFieldValue(e.target.name, uppercaseValue);
	};

	return (
		<Formik
			validationSchema={estimateSchemaGrey}
			innerRef={formikRef}
			onSubmit={async (values, { setSubmitting, setFieldError }) => {
				setErrorSubmit("");
				setSubmitting(true);
				try {
					const result = await axios.post("/api/msm/estimate", {
						registration: values.registration,
						noClaimsDuration: Number(values.noClaimsDuration) as number,
						driverAge: Number(values.driverAge) as number,
						homeOwner: values.homeOwner === "No" ? false : true,
						provisionalLicence: values.provisionalLicence === "No" ? false : true,
						postcode: values.postcode === UNDEFINED ? "" : values.postcode,
					});

					if (result) {
						const res = result.data.result as QuickWidgetResult;
						setResults(res);
						setReg(values.registration);
						setStep(2);
						setProfile({
							refId: "",
							email: "",
							registration: values.registration,
							noClaimsDuration: Number(values.noClaimsDuration),
							driverAge: Number(values.driverAge),
							provisionalLicence: values.provisionalLicence === "No" ? false : true,
							homeOwner: values.homeOwner === "No" ? false : true,
							postcode: values.postcode,
							upperQuote: res.upper,
							lowerQuote: res.lower,
							marketingConfirmation: false,
							renewalMonth: values.renewalMonth,
						});
					} else {
						setSubmitting(false);
						setErrorSubmit("Sorry, car registration or postcode is not recognised. Please check and re-enter the details");
					}
				} catch (e) {
					setSubmitting(false);
					setErrorSubmit("Sorry, car registration or postcode is not recognised. Please check and re-enter the details");
				}
				setSubmitting(false);
			}}
			initialValues={{
				registration: "",
				noClaimsDuration: "0",
				driverAge: "",
				homeOwner: "Yes",
				provisionalLicence: "No",
				postcode: "",
				renewalMonth: "",
			}}
			validateOnMount
		>
			{({ handleSubmit, values, errors, isSubmitting, handleChange, setFieldValue, touched, setFieldTouched, isValid }) => (
				<Form noValidate onSubmit={handleSubmit} className="px-4 py-[10px] pt-2 !text-[14px]">
					<div className="mb-2 flex flex-col items-start gap-2">
						<Form.Group className="w-full">
							<TextField
								name="registration"
								type="text"
								placeholder="Car registration"
								className="text-[14px] placeholder:text-[14px] placeholder:!font-bold placeholder:opacity-55"
								value={values.registration}
								handleChange={e => {
									handleChangeUppercase(e, setFieldValue);
								}}
								onBlur={() => setFieldTouched("registration")}
								error={touched.registration ? errors.registration : ""}
								required
								hasAsterisk
								hasErrorMessage={false}
							/>
						</Form.Group>
						<Form.Group className="w-full">
							<TextField
								name="postcode"
								type="text"
								placeholder="Postcode"
								className="text-[14px] placeholder:text-[14px] placeholder:!font-bold placeholder:opacity-55"
								value={values.postcode}
								handleChange={e => {
									handleChangeUppercase(e, setFieldValue);
								}}
								onBlur={() => setFieldTouched("postcode")}
								error={touched.postcode ? errors.postcode : ""}
								required
								hasAsterisk
								hasErrorMessage={false}
							/>
						</Form.Group>
					</div>
					<div className="mb-2 flex flex-col items-start gap-2">
						<Form.Group className="w-full">
							<TextField
								name="driverAge"
								type="number"
								placeholder="Age"
								className="text-[14px] placeholder:text-[14px] placeholder:!font-bold placeholder:opacity-55"
								value={values.driverAge}
								handleChange={handleChange}
								error={touched.driverAge ? errors.driverAge : ""}
								onBlur={() => setFieldTouched("driverAge")}
								required
								hasAsterisk
								hasErrorMessage={false}
							/>
						</Form.Group>
						<Form.Group className="w-full">
							<Form.Label className="font-semibold">Month of insurance renewal</Form.Label>
							<SelectFieldNew
								name="renewalMonth"
								value={values.renewalMonth && values.renewalMonth.toString()}
								className="text-md placeholder:!font-bold placeholder:opacity-55"
								handleChange={handleChange}
								options={MONTHS_OPTIONS}
							/>
						</Form.Group>
						<Form.Group className="w-full">
							<Form.Label className="font-semibold">No claims bonus</Form.Label>
							<SelectFieldNew
								name="noClaimsDuration"
								value={values.noClaimsDuration.toString()}
								className="text-md placeholder:!font-bold placeholder:opacity-55"
								handleChange={handleChange}
								options={[
									{
										value: "0",
										label: "0 year",
									},
									...Array.from({ length: 30 }, (_, index) => ({
										value: (index + 1).toString(),
										label: `${index + 1} year${index === 0 ? "" : "s"}`,
									})),
								]}
							/>
						</Form.Group>
					</div>
					<div className="flex flex-initial flex-col items-end gap-2">
						<Form.Group className="w-full">
							<Form.Label className="font-semibold">Home owner</Form.Label>
							<SelectFieldNew
								name="homeOwner"
								value={values.homeOwner}
								className="text-md  placeholder:!font-bold placeholder:opacity-55"
								handleChange={handleChange}
								options={[
									{
										value: "Yes",
										label: "Yes",
									},
									{
										value: "No",
										label: "No",
									},
								]}
							/>
						</Form.Group>
						<Form.Group className="w-full">
							<Form.Label className="font-semibold">Licence Type</Form.Label>
							<SelectFieldNew
								name="provisionalLicence"
								value={values.provisionalLicence}
								className="text-md"
								handleChange={handleChange}
								options={[
									{
										value: "Yes",
										label: "Provisional licence",
									},
									{
										value: "No",
										label: "Full licence",
									},
								]}
							/>
						</Form.Group>
					</div>

					<div className="mb-1 mt-[2.5rem]">
						<PrimaryCta
							// disabled={!isValid}
							isSubmitting={isSubmitting}
							type="submit"
							text={variant === "base" ? "Get Results" : "Continue"}
							hasArrowRight
							onClick={() => {
								handleSubmit();

								const formErrors = getFormikErrors(errors);
								if (formErrors.length > 0) {
									console.log(formErrors);
									setErrorMessages(formErrors);
									setOpen(true);
								}
							}}
							className="mx-auto px-5 !text-[14px] mobileL:ml-auto mobileL:mr-auto tablet:!w-[70vw]"
						/>
					</div>
					{errorSubmit && (
						<div className="mb-2 mt-2">
							<p className="text-center text-red">{errorSubmit} </p>
						</div>
					)}
					<ErrorToast errorMessages={errorMessages} isError={isValid ? false : true} setOpen={setOpen} open={open} />
				</Form>
			)}
		</Formik>
	);
};

export default QWPartnerForm;
